import { useEffect, useState } from "react";
import { api, eur, openBlob } from "../lib/api";
import { FileDown } from "lucide-react";
import { PageTitle } from "../components/common";
import { DateRange, monthStart, today, rangeParams } from "../components/DateRange";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { SplitBlock } from "../components/Split";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";

const MONTHS = { fr: "fr-BE", nl: "nl-BE", en: "en-GB" };

function Trend() {
  const { t, lang } = useLang();
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/dashboard/monthly").then((r) => setRows(r.data));
  }, []);
  const data = rows.map((r) => ({ ...r, label: new Date(`${r.period}-15`).toLocaleDateString(MONTHS[lang], { month: "short", year: "2-digit" }) }));
  return (
    <div className="tl-card mb-5" data-testid="dashboard-trend">
      <h3 className="font-bold uppercase mb-2">{t("db.trend")}</h3>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} width={48} />
            <Tooltip formatter={(v) => eur(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="ups" name={t("db.ups")} fill="#0f172a" radius={[3, 3, 0, 0]} />
            <Bar dataKey="sub" name={t("db.subTotal")} fill="#94a3b8" radius={[3, 3, 0, 0]} />
            <Line dataKey="margin" name={t("db.margin")} stroke="#d97706" strokeWidth={3} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, tone, testid }) {
  return (
    <div className={`tl-card tl-fade ${tone}`} data-testid={testid}>
      <div className="text-xs uppercase font-bold tracking-wider opacity-70">{label}</div>
      <div className="text-3xl font-bold tl-mono mt-1">{value}</div>
      {sub && <div className="text-xs mt-1 opacity-80">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const [range, setRange] = useState({ from: monthStart(), to: today() });
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/dashboard", { params: rangeParams(range) }).then((r) => setD(r.data));
  }, [range]);
  const pct = (m, u) => (u ? `${((m / u) * 100).toFixed(1).replace(".", ",")} %` : "—");
  const exp = (fmt) => {
    const q = new URLSearchParams({ format: fmt, ...(range.from && { date_from: range.from }), ...(range.to && { date_to: range.to }) });
    openBlob(`/dashboard/export?${q}`, `TourLink_tableau_de_bord_${range.from}_${range.to}.${fmt}`, true);
  };
  return (
    <div>
      <PageTitle title={t("db.title")} sub={t("db.sub")}>
        <DateRange value={range} onChange={setRange} testid="dashboard-range" />
        <button className="tl-btn tl-btn-dark tl-btn-sm" onClick={() => exp("pdf")} data-testid="dashboard-export-pdf"><FileDown size={16} /> PDF</button>
        <button className="tl-btn tl-btn-ghost tl-btn-sm" onClick={() => exp("xlsx")} data-testid="dashboard-export-xlsx"><FileDown size={16} /> Excel</button>
      </PageTitle>
      {d && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <Kpi label={t("db.ups")} value={eur(d.ups)} sub={d.ups_tbc ? `${d.ups_tbc} ${t("db.tbc")}` : ""} tone="" testid="kpi-ups" />
            <Kpi label={t("db.subTotal")} value={eur(d.sub)} sub={d.sub_tbc ? `${d.sub_tbc} ${t("db.tbc")}` : ""} tone="" testid="kpi-sub" />
            <Kpi label={t("db.margin")} value={eur(d.ups - d.sub)} sub={pct(d.ups - d.sub, d.ups)} tone={d.ups - d.sub < 0 ? "bg-red-50" : "bg-emerald-50"} testid="kpi-margin" />
            <Kpi label={t("db.prestations")} value={d.count} sub={`${d.validated} ${t("db.validated")} · ${d.to_control} ${t("db.toControl").toLowerCase()}`} tone="" testid="kpi-count" />
          </div>
          <Trend />
          {user.role === "admin" && <SplitBlock range={range} />}
          <div className="tl-card overflow-x-auto">
            <h3 className="font-bold uppercase mb-2">{t("db.byCompany")}</h3>
            <table className="tl-table" data-testid="dashboard-companies">
              <thead><tr><th>{t("c.company")}</th><th>{t("db.prestations")}</th><th>{t("db.ups")}</th><th>{t("db.subTotal")}</th><th>{t("db.margin")}</th><th>{t("db.marginPct")}</th></tr></thead>
              <tbody>
                {d.companies.map((c) => (
                  <tr key={c.company_id} data-testid={`dashboard-company-${c.company_id}`}>
                    <td>{c.company_name}</td><td>{c.count}</td>
                    <td className="tl-mono">{eur(c.ups)}</td><td className="tl-mono">{eur(c.sub)}</td>
                    <td className={`tl-mono font-semibold ${c.ups - c.sub < 0 ? "text-red-700" : "text-emerald-700"}`}>{eur(c.ups - c.sub)}</td>
                    <td className="tl-mono">{pct(c.ups - c.sub, c.ups)}</td>
                  </tr>
                ))}
                {!d.companies.length && <tr><td colSpan={6} className="text-slate-500">—</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
